import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { platformClient } from "@homy/api-client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Input,
  Label,
  Badge,
  Alert,
  AlertDescription,
} from "@homy/ui";
import {
  UserCheck,
  ShieldAlert,
  Copy,
  Check,
  ExternalLink,
  Building2,
  Users,
  Search,
  ArrowRight,
  Shield,
  RotateCcw,
  CheckCircle2,
} from "lucide-react";

export function ImpersonationModal({ open, onOpenChange, initialOrgId = "" }) {
  const [selectedOrgId, setSelectedOrgId] = useState(initialOrgId);
  const [selectedUser, setSelectedUser] = useState(null);
  const [orgSearch, setOrgSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [reason, setReason] = useState("Diagnostic support investigation");
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // 1. Fetch pre-populated organizations
  const {
    data: orgsData,
    isLoading: orgsLoading,
  } = useQuery({
    queryKey: ["platform-orgs-for-impersonation"],
    queryFn: async () => {
      const res = await platformClient.get("/organizations", { params: { limit: 100 } });
      return res.data?.items || [];
    },
    enabled: open,
  });

  // Filter organizations by search (min 3 chars if searching, otherwise show all)
  const filteredOrgs = useMemo(() => {
    if (!orgsData) return [];
    if (!orgSearch.trim()) return orgsData;
    if (orgSearch.trim().length < 3) return orgsData;
    const term = orgSearch.toLowerCase();
    return orgsData.filter(
      (org) =>
        org.name.toLowerCase().includes(term) ||
        (org.slug && org.slug.toLowerCase().includes(term)) ||
        (org.countryCode && org.countryCode.toLowerCase().includes(term))
    );
  }, [orgsData, orgSearch]);

  // Set initial selected organization if provided
  useEffect(() => {
    if (initialOrgId && !selectedOrgId) {
      setSelectedOrgId(initialOrgId);
    }
  }, [initialOrgId, selectedOrgId]);

  // 2. Fetch users of the selected organization
  const {
    data: usersData,
    isLoading: usersLoading,
  } = useQuery({
    queryKey: ["platform-org-users-for-impersonation", selectedOrgId, userSearch],
    queryFn: async () => {
      if (!selectedOrgId) return [];
      const params = {};
      if (userSearch.trim().length >= 3) {
        params.search = userSearch.trim();
      }
      const res = await platformClient.get(`/organizations/${selectedOrgId}/users`, { params });
      return res.data?.items || [];
    },
    enabled: Boolean(selectedOrgId) && open,
  });

  // Filter users locally if search is < 3 chars or after fetch
  const filteredUsers = useMemo(() => {
    if (!usersData) return [];
    if (!userSearch.trim()) return usersData;
    const term = userSearch.toLowerCase();
    return usersData.filter(
      (u) =>
        u.fullName.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term) ||
        u.role.toLowerCase().includes(term)
    );
  }, [usersData, userSearch]);

  const selectedOrg = useMemo(() => {
    return orgsData?.find((o) => o.id === selectedOrgId);
  }, [orgsData, selectedOrgId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      setError("Please select a tenant member to impersonate.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await platformClient.post("/impersonation/token", {
        target_user_id: selectedUser.id,
        target_organization_id: selectedOrgId || undefined,
        reason: reason.trim(),
        is_read_only: isReadOnly,
      });
      setResult(res.data);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          "Failed to generate impersonation token"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (result?.token) {
      navigator.clipboard.writeText(result.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleLaunchWeb = () => {
    if (!result?.token) return;
    const appBase =
      import.meta.env.VITE_APP_URL ||
      (typeof window !== "undefined" &&
      (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
        ? "http://127.0.0.1:3010"
        : "https://app.homyos.app");
    const url = `${appBase.replace(/\/$/, "")}/login?token=${encodeURIComponent(result.token)}${result.refreshToken ? `&refreshToken=${encodeURIComponent(result.refreshToken)}` : ""}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleReset = () => {
    setSelectedOrgId("");
    setSelectedUser(null);
    setOrgSearch("");
    setUserSearch("");
    setResult(null);
    setError(null);
    setCopied(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleReset();
        onOpenChange(isOpen);
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 text-primary">
            <UserCheck className="h-5 w-5" />
            <DialogTitle>Diagnostic Impersonation Launcher</DialogTitle>
          </div>
          <DialogDescription className="text-xs">
            Generate a short-lived (30-minute) diagnostic session token to inspect tenant issues
            without credential sharing.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" className="my-2">
            <ShieldAlert className="h-4 w-4" />
            <AlertDescription className="text-xs">{error}</AlertDescription>
          </Alert>
        )}

        {!result ? (
          <form onSubmit={handleSubmit} className="space-y-4 py-2">
            {/* Step 1: Organization Selection */}
            <div className="space-y-2 border border-border/70 rounded-xl p-3 bg-card/40">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold flex items-center gap-1.5 uppercase tracking-wider text-muted-foreground">
                  <Building2 className="h-3.5 w-3.5 text-primary" />
                  1. Select Tenant Organization
                </Label>
                {selectedOrg && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setSelectedOrgId("");
                      setSelectedUser(null);
                    }}
                  >
                    Change Organization
                  </Button>
                )}
              </div>

              {!selectedOrg ? (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Search organization by name or slug (min 3 chars)..."
                      value={orgSearch}
                      onChange={(e) => setOrgSearch(e.target.value)}
                      className="pl-8 h-8 text-xs bg-background"
                    />
                  </div>

                  {orgsLoading ? (
                    <div className="h-20 flex items-center justify-center text-xs text-muted-foreground font-mono">
                      Loading organizations...
                    </div>
                  ) : (
                    <div className="max-h-40 overflow-y-auto divide-y divide-border/40 rounded-lg border border-border/50 bg-background/50">
                      {filteredOrgs.map((org) => (
                        <button
                          key={org.id}
                          type="button"
                          onClick={() => {
                            setSelectedOrgId(org.id);
                            setSelectedUser(null);
                          }}
                          className="w-full text-left p-2.5 hover:bg-muted/40 transition-colors flex items-center justify-between text-xs"
                        >
                          <div>
                            <span className="font-semibold text-foreground">{org.name}</span>
                            <span className="text-[10px] text-muted-foreground font-mono ml-2">
                              {org.slug || org.id}
                            </span>
                          </div>
                          <Badge variant="outline" className="text-[10px]">
                            {org.countryCode} • {org.status}
                          </Badge>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-primary/10 border border-primary/30">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" />
                    <div>
                      <span className="font-semibold text-xs text-foreground">{selectedOrg.name}</span>
                      <span className="text-[10px] text-muted-foreground font-mono ml-2">
                        ({selectedOrg.countryCode}) • {selectedOrg.slug}
                      </span>
                    </div>
                  </div>
                  <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 text-[10px]">
                    Selected
                  </Badge>
                </div>
              )}
            </div>

            {/* Step 2: Member Selection within the Organization */}
            {selectedOrgId && (
              <div className="space-y-2 border border-border/70 rounded-xl p-3 bg-card/40">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold flex items-center gap-1.5 uppercase tracking-wider text-muted-foreground">
                    <Users className="h-3.5 w-3.5 text-primary" />
                    2. Select Tenant Member
                  </Label>
                  <span className="text-[10px] text-muted-foreground">
                    {filteredUsers.length} members found
                  </span>
                </div>

                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search member by name, email or role (min 3 chars)..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-8 h-8 text-xs bg-background"
                  />
                </div>

                {usersLoading ? (
                  <div className="h-20 flex items-center justify-center text-xs text-muted-foreground font-mono">
                    Loading organization members...
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="h-16 flex items-center justify-center text-xs text-muted-foreground">
                    No members found for this organization matching the query.
                  </div>
                ) : (
                  <div className="max-h-44 overflow-y-auto divide-y divide-border/40 rounded-lg border border-border/50 bg-background/50">
                    {filteredUsers.map((u) => {
                      const isSelected = selectedUser?.id === u.id;
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => setSelectedUser(u)}
                          className={`w-full text-left p-2.5 transition-colors flex items-center justify-between text-xs ${
                            isSelected
                              ? "bg-primary/15 border-l-2 border-primary"
                              : "hover:bg-muted/40"
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground">
                              {u.fullName
                                ? u.fullName.split(" ").map((n) => n[0]).join("").slice(0, 2)
                                : "U"}
                            </div>
                            <div>
                              <div className="font-semibold text-foreground flex items-center gap-1.5">
                                {u.fullName}
                                {u.isPlatformAdmin && (
                                  <Badge variant="outline" className="text-[9px] py-0 text-amber-400">
                                    Platform Admin
                                  </Badge>
                                )}
                              </div>
                              <span className="text-[10px] text-muted-foreground font-mono">
                                {u.email}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] font-mono">
                              {u.role}
                            </Badge>
                            {isSelected && (
                              <CheckCircle2 className="h-4 w-4 text-primary" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Diagnostic Configuration */}
            {selectedUser && (
              <div className="space-y-3 pt-1">
                <div className="space-y-1.5">
                  <Label htmlFor="impersonate-reason" className="text-xs">Audit Justification / Ticket # *</Label>
                  <Input
                    id="impersonate-reason"
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="E.g. Investigating dossier submission failure (TICKET-452)"
                    className="text-xs bg-background h-8"
                    required
                  />
                </div>

                <div className="flex items-center justify-between p-2.5 rounded-lg border border-border bg-card/60">
                  <div className="space-y-0.5">
                    <span className="text-xs font-medium text-foreground block">
                      Read-Only Diagnostic Guard
                    </span>
                    <span className="text-[10px] text-muted-foreground block">
                      Enforces HTTP 403 blocking on all mutations (POST, PUT, PATCH, DELETE).
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    id="read-only-toggle"
                    checked={isReadOnly}
                    onChange={(e) => setIsReadOnly(e.target.checked)}
                    className="h-4 w-4 accent-primary rounded cursor-pointer"
                  />
                </div>
              </div>
            )}

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                className="gap-1.5 text-xs"
                disabled={loading || !selectedUser}
              >
                <UserCheck className="h-3.5 w-3.5" />
                {loading ? "Generating Token..." : "Launch Impersonation"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          /* Result View */
          <div className="space-y-4 py-2">
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 space-y-1">
              <div className="flex items-center gap-1.5 font-semibold text-xs">
                <Check className="h-4 w-4" />
                Diagnostic Session Active
              </div>
              <p className="text-[11px] text-emerald-300/80">
                Impersonating <strong className="text-foreground">{selectedUser?.fullName}</strong> ({selectedUser?.email}) in organization <strong className="text-foreground">{selectedOrg?.name}</strong>.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Cryptographic Bearer Token</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={result.token}
                  className="font-mono text-[10px] select-all bg-muted"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-1 text-xs"
                  onClick={handleCopy}
                >
                  {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground">
                Expires in {result.expiresIn ? Math.round(result.expiresIn / 60) : 30} minutes (30-min refresh token included).
              </p>
            </div>

            <div className="p-3 rounded-lg border border-border bg-card/40 space-y-1.5 text-xs">
              <div className="flex justify-between text-[11px]">
                <span className="text-muted-foreground">Diagnostic Scope:</span>
                <span className="font-semibold text-foreground">
                  {isReadOnly ? "Read-Only (Mutations Blocked)" : "Full Write Access"}
                </span>
              </div>
              <div className="flex justify-between text-[11px]">
                <span className="text-muted-foreground">Target Organization ID:</span>
                <span className="font-mono text-[10px] text-muted-foreground">{selectedOrgId}</span>
              </div>
              <div className="flex justify-between text-[11px]">
                <span className="text-muted-foreground">Token Claims:</span>
                <span className="font-mono text-[10px] text-amber-500 font-semibold flex items-center gap-1">
                  isImpersonated: true • 30m TTL (30m refresh token) {result.isReadOnly && "• isReadOnly: true"}
                </span>
              </div>
              <div className="flex justify-between text-[11px]">
                <span className="text-muted-foreground">Audit Log Event:</span>
                <span className="font-mono text-[10px] text-emerald-400">IMPERSONATION_STARTED</span>
              </div>
            </div>

            <DialogFooter className="pt-2 flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={handleReset}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Impersonate Another User
              </Button>
              <Button
                type="button"
                size="sm"
                className="gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-500 text-white"
                onClick={handleLaunchWeb}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Launch in Application
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ImpersonationModal;
